import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';

interface RefreshButtonProps {
  /** Query keys to invalidate when refresh is clicked */
  queryKeys: string[];
  /** Optional tooltip text */
  tooltip?: string;
  /** Optional callback after refresh */
  onRefresh?: () => void;
  /** Size of the button - 'sm' | 'md' | 'lg' */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the button is in a loading state */
  isLoading?: boolean;
}

/**
 * A reusable refresh button that invalidates React Query cache
 * Use this for manual data refresh in list pages and detail views
 */
const RefreshButton: React.FC<RefreshButtonProps> = ({
  queryKeys,
  tooltip = 'Refresh data',
  onRefresh,
  size = 'md',
  isLoading = false
}) => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);

    // Invalidate and refetch all provided query keys
    // Using refetchType: 'all' ensures even inactive queries are refetched
    await Promise.all(
      queryKeys.map(key =>
        queryClient.invalidateQueries({
          queryKey: [key],
          refetchType: 'all'
        })
      )
    );

    // Call optional callback
    onRefresh?.();

    // Brief delay to show the animation
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  const buttonSizeClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2'
  };

  const spinning = isRefreshing || isLoading;

  return (
    <button
      type="button"
      onClick={handleRefresh}
      disabled={spinning}
      className={`
        ${buttonSizeClasses[size]}
        text-gray-500 hover:text-gray-700 hover:bg-gray-100
        rounded-md transition-colors focus:outline-none focus:ring-2
        focus:ring-indigo-500 focus:ring-offset-1
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
      title={tooltip}
      aria-label={tooltip}
    >
      <ArrowPathIcon
        className={`
          ${sizeClasses[size]}
          ${spinning ? 'animate-spin' : ''}
        `}
      />
    </button>
  );
};

export default RefreshButton;
